import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { OBJECT_TYPE_LABELS } from '../../constants';
import { formatDateTime } from '../../helpers';
import { useApprovalDiff } from '../../hooks/useApprovalDiff';
import type { ApprovalRequest } from '../../types';
import { computeTextLines, diffTotals, sectionStats } from './computeTextDiff';
import { CompareHeader } from './CompareHeader';
import { CompareSidebar } from './CompareSidebar';
import { FieldsDiff } from './FieldsDiff';
import { FilesDiff } from './FilesDiff';
import { ItemsDiff } from './ItemsDiff';
import { SectionShell } from './SectionShell';
import { TextDiff } from './TextDiff';

interface CompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: ApprovalRequest;
  canDecide: boolean;
  onApprove: () => void;
  approveLoading?: boolean;
  onReject: () => void;
}

const CompareDialog = ({
  open,
  onOpenChange,
  request,
  canDecide,
  onApprove,
  approveLoading,
  onReject,
}: CompareDialogProps) => {
  const [base, setBase] = useState<string | undefined>(undefined);
  const [split, setSplit] = useState(true);
  const [onlyChanges, setOnlyChanges] = useState(true);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const curRef = useRef(-1);
  const [pendingSection, setPendingSection] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setBase(undefined);
    setCollapsed({});
    curRef.current = -1;
  }, [request.id]);

  const { data, isLoading, isError, isFetching, error } = useApprovalDiff(request, base, open);
  const errorStatus = axios.isAxiosError(error) ? error.response?.status : undefined;
  const unavailable = errorStatus === 404 || errorStatus === 405;
  const diff = isError ? undefined : data;

  const computed = useMemo(() => {
    if (!diff) return [];
    return diff.sections.map((sec) => {
      const lines = sec.kind === 'text' ? computeTextLines(sec.old, sec.new) : undefined;
      return { sec, lines, stats: sectionStats(sec, lines) };
    });
  }, [diff]);

  const totals = useMemo(() => diffTotals(computed.map((c) => c.stats)), [computed]);
  const changed = computed.filter(({ stats }) => stats.add + stats.del + stats.mod > 0);
  const visibleSections = onlyChanges ? changed : computed;

  useEffect(() => {
    curRef.current = -1;
  }, [diff, onlyChanges, collapsed, split]);

  useEffect(() => {
    if (!pendingSection) return;
    const root = bodyRef.current;
    const el = Array.from(root?.querySelectorAll<HTMLElement>('[data-diff-section]') ?? []).find(
      (node) => node.dataset.diffSection === pendingSection
    );
    if (root && el)
      root.scrollTo({
        top:
          el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - 12,
        behavior: 'smooth',
      });
    setPendingSection(null);
  }, [pendingSection, collapsed]);

  const jump = (dir: 1 | -1) => {
    const root = bodyRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-diff-change]'));
    if (!nodes.length) return;
    const next =
      curRef.current < 0
        ? dir === 1
          ? 0
          : nodes.length - 1
        : (curRef.current + dir + nodes.length) % nodes.length;
    curRef.current = next;
    const el = nodes[next];
    root.scrollTo({
      top:
        el.getBoundingClientRect().top -
        root.getBoundingClientRect().top +
        root.scrollTop -
        root.clientHeight / 3,
      behavior: 'smooth',
    });
    el.animate(
      [
        { boxShadow: 'inset 0 0 0 2px rgba(0,120,229,.55)' },
        { boxShadow: 'inset 0 0 0 2px rgba(0,120,229,0)' },
      ],
      { duration: 900 }
    );
  };

  const scrollToSection = (id: string) => {
    setCollapsed((c) => ({ ...c, [id]: false }));
    setPendingSection(id);
  };

  const typeLabel = OBJECT_TYPE_LABELS[request.object_type] ?? request.object_type;
  const baseLabel = diff?.base ? `Původně — ${diff.base.label}` : 'Původně';
  const targetLabel = diff ? `Nově — ${diff.target.label}` : 'Nově';
  const targetWho = diff?.target.created_by_name ?? undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        data-testid="compare-dialog"
        className="sm:max-w-[1180px] w-[calc(100vw-3rem)] h-[min(880px,calc(100vh-3rem))] p-0 gap-0"
      >
        {!diff ? (
          <>
            <div className="flex items-center justify-between border-b px-5 py-3">
              <DialogTitle>Porovnání verzí</DialogTitle>
              <DialogClose asChild>
                <Button variant="ghost" data-testid="compare-close-btn">
                  Zavřít
                </Button>
              </DialogClose>
            </div>
            <div className="flex-1 flex items-center justify-center">
              {isLoading ? (
                <SpinnerOverlay />
              ) : (
                <div className="text-sm text-neutral-500">
                  {unavailable
                    ? 'Porovnání verzí pro tuto žádost není na serveru dostupné.'
                    : isError
                    ? 'Porovnání se nepodařilo načíst.'
                    : 'Porovnání není k dispozici.'}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <CompareHeader
              diff={diff}
              typeLabel={typeLabel}
              targetWho={targetWho}
              totals={totals}
              changedSections={changed.length}
              canJump={visibleSections.some(
                ({ sec, stats }) => !collapsed[sec.id] && stats.add + stats.del + stats.mod > 0
              )}
              onBaseChange={setBase}
              onlyChanges={onlyChanges}
              onOnlyChangesToggle={() => setOnlyChanges((v) => !v)}
              split={split}
              onSplitChange={setSplit}
              onJump={jump}
            />

            <div className="flex-1 min-h-0 flex">
              <CompareSidebar
                onlyChanges={onlyChanges}
                sections={visibleSections.map(({ sec, stats }) => ({
                  id: sec.id,
                  label: sec.label,
                  stats,
                  kind: sec.kind,
                }))}
                onNavigate={scrollToSection}
              />

              <div
                ref={bodyRef}
                className="relative flex-1 min-w-0 overflow-y-auto bg-neutral-50 p-3 space-y-3"
              >
                {isFetching && <SpinnerOverlay />}
                {diff.warning && (
                  <div className="flex items-start gap-2.5 rounded border border-amber-200 bg-amber-50 px-3 py-2">
                    <AlertTriangle size={14} className="mt-0.5 text-amber-600 shrink-0" />
                    <div className="text-[12.5px] text-amber-900 leading-relaxed">
                      {diff.warning}
                    </div>
                  </div>
                )}
                {visibleSections.length === 0 && (
                  <div className="p-4 text-sm text-neutral-500">Beze změn</div>
                )}
                {visibleSections.map(({ sec, lines, stats }) => (
                  <SectionShell
                    key={sec.id}
                    id={sec.id}
                    label={sec.label}
                    kind={sec.kind}
                    stats={stats}
                    collapsed={!!collapsed[sec.id]}
                    onToggle={() => setCollapsed((c) => ({ ...c, [sec.id]: !c[sec.id] }))}
                  >
                    {((sec.kind === 'text' && !sec.old && !sec.new) ||
                      (sec.kind !== 'text' && sec.rows.length === 0)) && (
                      <div className="p-3 text-sm text-neutral-500">
                        {sec.kind === 'text' ? 'Beze změn' : 'Žádné položky'}
                      </div>
                    )}
                    {sec.kind === 'items' && (
                      <ItemsDiff rows={sec.rows} onlyChanges={onlyChanges} />
                    )}
                    {sec.kind === 'fields' && (
                      <FieldsDiff
                        rows={sec.rows}
                        onlyChanges={onlyChanges}
                        baseLabel={baseLabel}
                        targetLabel={targetLabel}
                      />
                    )}
                    {sec.kind === 'text' && lines && (
                      <TextDiff
                        lines={lines}
                        split={split}
                        onlyChanges={false}
                        baseLabel={baseLabel}
                        targetLabel={targetLabel}
                      />
                    )}
                    {sec.kind === 'files' && (
                      <FilesDiff rows={sec.rows} onlyChanges={onlyChanges} />
                    )}
                  </SectionShell>
                ))}
              </div>
            </div>

            <div className="px-5 py-3 border-t border-neutral-150 bg-neutral-25 flex items-center gap-2 shrink-0">
              <span className="text-[11.5px] text-neutral-500">
                Verzi <span className="font-mono text-neutral-800">{diff.target.label}</span>{' '}
                {targetWho ? (
                  <>
                    podal(a) <span className="text-neutral-800">{targetWho}</span> ·{' '}
                  </>
                ) : (
                  'podáno '
                )}
                {formatDateTime(diff.target.created_on)}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <DialogClose asChild>
                  <Button variant="ghost" data-testid="compare-close-btn">
                    Zavřít
                  </Button>
                </DialogClose>
                {canDecide && (
                  <>
                    <Button
                      variant="ghost"
                      className="text-danger hover:bg-red-50"
                      onClick={onReject}
                      disabled={isFetching || approveLoading}
                      data-testid="compare-reject-btn"
                    >
                      <X size={13} />
                      Zamítnout
                    </Button>
                    <Button
                      onClick={onApprove}
                      disabled={isFetching || approveLoading}
                      data-testid="compare-approve-btn"
                    >
                      <Check size={13} strokeWidth={2.5} />
                      Schválit změny
                    </Button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompareDialog;
